/**
 * Material resolution helpers bridging the document material registry to the
 * three.js renderers. Everything is defensive: an unknown or legacy layer
 * materialId (plain string like 'Gypsum') still resolves when a material with
 * that NAME exists; otherwise renderers keep their function-based fallback.
 */

import * as THREE from 'three';
import type { AppearanceAsset, BIMMaterial } from '../core/MaterialTypes';
import { getFillPattern } from '../core/MaterialTypes';

export interface MaterialProjectSlice {
    materials?: Map<string, BIMMaterial>;
    appearanceAssets?: Map<string, AppearanceAsset>;
}

/** Registry lookup by id first, then case-insensitive name. */
export function resolveMaterial(
    project: MaterialProjectSlice,
    key: string | null | undefined,
): BIMMaterial | undefined {
    if (!key || !project.materials) return undefined;
    const direct = project.materials.get(key);
    if (direct) return direct;
    const lower = key.toLowerCase();
    for (const m of project.materials.values()) {
        if (m.name.toLowerCase() === lower) return m;
    }
    return undefined;
}

export function resolveAppearance(
    project: MaterialProjectSlice,
    material: BIMMaterial | undefined,
): AppearanceAsset | undefined {
    if (!material || !project.appearanceAssets) return undefined;
    return project.appearanceAssets.get(material.appearanceAssetId);
}

/**
 * Effective 2D/3D color for a material: render-appearance color when
 * "Use Render Appearance" is on, otherwise the explicit shading color.
 */
export function materialColor(material: BIMMaterial | undefined, asset: AppearanceAsset | undefined): string | undefined {
    if (!material) return undefined;
    if (material.graphics.useRenderAppearance && asset) return asset.color;
    return material.graphics.shadingColor;
}

/** One-call layer shading color; undefined when no registry material matches. */
export function layerMaterialColor(
    project: MaterialProjectSlice,
    layer: { materialId?: string } | null | undefined,
): string | undefined {
    const material = resolveMaterial(project, layer?.materialId);
    return materialColor(material, resolveAppearance(project, material));
}

// ==================== Texture cache (appearance images) ====================

const textureCache = new Map<string, THREE.Texture | null>();

/**
 * Load (and cache) the appearance image as a tiling texture. `url` is the
 * object/asset URL from the image library; tile meters drive repeat so one
 * image tile covers `tileSize` meters on a surface.
 */
export function appearanceTexture(
    url: string | undefined,
    tileSize: number,
): THREE.Texture | undefined {
    if (!url) return undefined;
    const cacheKey = `${url}|${tileSize}`;
    const cached = textureCache.get(cacheKey);
    if (cached === null) return undefined; // previously failed
    if (cached) return cached;

    const tex = new THREE.TextureLoader().load(url);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    const tile = tileSize > 0 ? tileSize : 1;
    tex.repeat.set(1 / tile, 1 / tile);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.userData = { onError: true };
    tex.addEventListener('error', () => {
        textureCache.set(cacheKey, null);
        tex.dispose();
    });
    textureCache.set(cacheKey, tex);
    return tex;
}

/** Hatch parameters derived from a material's graphics section. */
export function hatchParams(material: BIMMaterial | undefined) {
    if (!material) return undefined;
    const cut = getFillPattern(material.graphics.cutPatternId);
    if (!cut || cut.style === 'None') return undefined;
    return {
        style: cut.style,
        angle: (cut.angle * Math.PI) / 180,
        // Convert model spacing (m) into the shader's unit-less frequency.
        spacing: 1 / Math.max(0.002, cut.spacing),
        thickness: cut.thickness,
        foreground: material.graphics.foregroundColor,
        background: material.graphics.backgroundColor,
    };
}
